import React, { useState } from "react";

import { Modal, Form, Button, DropdownProps, InputOnChangeData, Divider, SemanticCOLORS } from "semantic-ui-react";

import ModalApp from "../ModalApp";
import { Roles } from "../../interfaces";
import { toast } from "react-toastify";
import { GetUser } from "../../services/auth";

type IFieldsType = "string" | "number" | "date" | "enum" | undefined;

type FilterRoles = Roles | "*";

export interface IFields {
    label: string;
    name: string;
    type: IFieldsType;
    values?: any[];
    roles: FilterRoles[];
}

interface IOperators {
    label: string;
    name: string;
    type: IFieldsType[];
}

interface ICombinators {
    label: string;
    name: string;
    color: SemanticCOLORS;
}

interface IFilter {
    field: string;
    operator: string;
    combinator: string;
    value: string;
}

interface IProps {
    fields: IFields[];
    onSuccess: (filter: string) => void;
}

const FilterBuilder: React.FC<IProps> = ({ fields, onSuccess }) => {

    const { role } = GetUser();

    const initialFilter: IFilter = {
        field: "",
        operator: "",
        combinator: "",
        value: ""
    }

    const [state, setState] = useState<IFilter[]>([]);
    const [filter, setFilter] = useState<IFilter>(initialFilter);

    const operators: IOperators[] = [
        { name: "Contains", label: "Contém", type: ["string"] },
        { name: "StartsWith", label: "Começa com", type: ["string"] },
        { name: "EndsWith", label: "Termina com", type: ["string"] },
        { name: "NContains", label: "Não contém", type: ["string"] },
        { name: "NStartsWith", label: "Não começa com", type: ["string"] },
        { name: "NEndsWith", label: "Não termina com", type: ["string"] },
        { name: "==", label: "Igual a (==)", type: ["number", "enum", "date"] },
        { name: "!=", label: "Diferente de (!=)", type: ["number", "enum", "date"] },
        { name: "<", label: "Menor (<)", type: ["number", "date"] },
        { name: ">", label: "Maior (>)", type: ["number", "date"] },
        { name: "<=", label: "Menor igual (<=)", type: ["number", "date"] },
        { name: ">=", label: "Maior igual (>=)", type: ["number", "date"] },
    ];

    const combinators: ICombinators[] = [
        { name: "&&", label: "E", color: "blue" },
        { name: "||", label: "OU", color: "orange" },
        { name: "END", label: "FIM", color: "green" },
    ];

    const handleOnOpen = () => {
        setState([]);
        setFilter(initialFilter);
    }

    const handleRemoveFilter = (indexRemove: number) => {
        const filters = state.filter((filter, index) => index !== indexRemove);

        setState(filters);
    }

    const handleOnChangeField = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { value } = data;
        setFilter(filter => ({ ...filter, field: value as string, operator: "", value: "" }));
    }


    const handleOnChangeOperator = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { value } = data;
        setFilter(filter => ({ ...filter, operator: value as string, value: "" }));
    }


    const handleOnChangeDropdownValue = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { value } = data;
        setFilter(filter => ({ ...filter, value: value as string }));
    }

    const handleOnChangeInputValue = (event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        const { value } = data;
        setFilter(filter => ({ ...filter, value }));
    }

    const handleSetCombinator = (combinator: string) => {
        if (filter.field === "" || filter.operator === "" || filter.value === "") {
            toast("É necessário preencher todos os campos para criar o filtro", { position: "top-center", type: "error" });
        } else {
            setState(state => ([...state, { ...filter, combinator }]));
            setFilter(initialFilter);
        }
    }

    const renderCombinators = ({ combinators, name }: { combinators: ICombinators[]; name?: string; }) => {
        if (name) {
            const combinator = combinators.find(combinator => combinator.name === name);
            return (
                <Form.Button
                    fluid
                    content={combinator?.label}
                    color={combinator?.color}
                />
            );
        } else {
            return (
                <Button.Group fluid>
                    {combinators.map((combinator, index) =>
                        <React.Fragment key={`combinators_${index}`}>
                            <Button
                                key={`combinator_button_${index}`}
                                content={combinator.label}
                                onClick={() => handleSetCombinator(combinator.name)}
                            />
                            {index < combinators.length - 1 &&
                                <Button.Or key={`combinator_or_${index}`} text="?" />
                            }
                        </React.Fragment>
                    )}
                </Button.Group>
            );
        }
    }

    const buildFilter = () => {
        let filterString = "";

        if (state.length > 0) {
            state.forEach((filter, index) => {
                switch (filter.operator) {
                    case "Contains":
                        filterString += `${filter.field}.${filter.operator}("${filter.value}")`;
                        break;
                    case "StartsWith":
                        filterString += `${filter.field}.${filter.operator}("${filter.value}")`;
                        break;
                    case "EndsWith":
                        filterString += `${filter.field}.${filter.operator}("${filter.value}")`;
                        break;
                    case "NContains":
                        filterString += `!${filter.field}.Contains("${filter.value}")`;
                        break;
                    case "NStarsWith":
                        filterString += `!${filter.field}.StartsWith("${filter.value}")`;
                        break;
                    case "NEndsWith":
                        filterString += `!${filter.field}.EndsWith("${filter.value}")`;
                        break;
                    case "==":
                        if (fields.find(field => field.name === filter.field)?.type === "date") {
                            filterString += `${filter.field} ${filter.operator} "${filter.value}"`;
                        } else {
                            filterString += `${filter.field} ${filter.operator} ${filter.value}`;
                        }
                        break;
                    case "!=":
                        if (fields.find(field => field.name === filter.field)?.type === "date") {
                            filterString += `${filter.field} ${filter.operator} "${filter.value}"`;
                        } else {
                            filterString += `${filter.field} ${filter.operator} ${filter.value}`;
                        }
                        break;
                    case "<":
                        if (fields.find(field => field.name === filter.field)?.type === "date") {
                            filterString += `${filter.field} ${filter.operator} "${filter.value}"`;
                        } else {
                            filterString += `${filter.field} ${filter.operator} ${filter.value}`;
                        }
                        break;
                    case ">":
                        if (fields.find(field => field.name === filter.field)?.type === "date") {
                            filterString += `${filter.field} ${filter.operator} "${filter.value}"`;
                        } else {
                            filterString += `${filter.field} ${filter.operator} ${filter.value}`;
                        }
                        break;
                    case "<=":
                        if (fields.find(field => field.name === filter.field)?.type === "date") {
                            filterString += `${filter.field} ${filter.operator} "${filter.value}"`;
                        } else {
                            filterString += `${filter.field} ${filter.operator} ${filter.value}`;
                        }
                        break;
                    case ">=":
                        if (fields.find(field => field.name === filter.field)?.type === "date") {
                            filterString += `${filter.field} ${filter.operator} "${filter.value}"`;
                        } else {
                            filterString += `${filter.field} ${filter.operator} ${filter.value}`;
                        }
                        break;
                    default:
                        break;
                }

                switch (filter.combinator) {
                    case "&&":
                        if (index < state.length - 1) {
                            filterString += " && ";
                        }
                        break;
                    case "||":
                        if (index < state.length - 1) {
                            filterString += " || ";
                        }
                        break;
                    default:
                        break;
                }
            });
        } else {
            toast("É necessário criar um filtro primeiro para finalizar", { position: "top-center", type: "error" });
        }

        return filterString;
    }

    const _PreDefinedValues = fields.filter(field => field.roles.includes(role) || field.roles.includes("*")).find(field => field.name === filter.field)?.values;

    return (
        <ModalApp
            title="Filtro"
            trigger={{
                icon: "filter",
                color: "pink"
            }}
            onOpen={handleOnOpen}
            content={
                <Modal.Content>
                    <Form>
                        {state.length > 0 &&
                            <>
                                {state.map((filter, index) =>
                                    <Form.Group key={`filterbuilder_${index}`} widths="equal">
                                        <Form.Dropdown
                                            selection
                                            open={false}
                                            options={fields.map(field => { return { key: field.label, text: field.label, value: field.name } })}
                                            value={filter.field}
                                        />
                                        <Form.Dropdown
                                            selection
                                            open={false}
                                            options={operators.map(operator => { return { key: operator.label, text: operator.label, value: operator.name } })}
                                            value={filter.operator}
                                        />
                                        <Form.Input
                                            readOnly
                                            value={filter.value}
                                        />
                                        {renderCombinators({ combinators: combinators, name: filter.combinator })}
                                        <Form.Button fluid width="1" icon="trash" color="red" onClick={() => handleRemoveFilter(index)} />
                                    </Form.Group>
                                )}
                                {state[state.length - 1].combinator !== "END" && <Divider />}
                            </>
                        }
                        {(state[state.length - 1] || { combinator: "START" }).combinator !== "END" &&
                            <Form.Group widths="equal">
                                <Form.Dropdown
                                    selection
                                    placeholder="Campo"
                                    options={
                                        fields.filter(field => field.roles.includes(role) || field.roles.includes("*"))
                                            .map(field => { return { key: field.label, text: field.label, value: field.name } })
                                    }
                                    value={filter.field}
                                    onChange={handleOnChangeField}
                                />
                                <Form.Dropdown
                                    selection
                                    placeholder="Filtro"
                                    disabled={filter.field === ""}
                                    options={
                                        operators.filter(operator => operator.type.includes(fields.find(field => field.name === filter.field)?.type))
                                            .map(operator => { return { key: operator.label, text: operator.label, value: operator.name } })
                                    }
                                    value={filter.operator}
                                    onChange={handleOnChangeOperator}
                                />
                                {_PreDefinedValues !== undefined &&
                                    <Form.Dropdown
                                        selection
                                        placeholder="Valor"
                                        disabled={filter.operator === ""}
                                        options={_PreDefinedValues.map(value => { return { key: value.key, text: value.text, value: value.value, label: value.label } })}
                                        value={filter.value}
                                        onChange={handleOnChangeDropdownValue}
                                    />}
                                {_PreDefinedValues === undefined &&
                                    <Form.Input
                                        placeholder="Valor"
                                        disabled={filter.operator === ""}
                                        value={filter.value}
                                        onChange={handleOnChangeInputValue}
                                    />}
                                {renderCombinators({ combinators: combinators })}
                            </Form.Group>
                        }
                    </Form>
                </Modal.Content>
            }
            onSuccess={{
                content: "Ok",
                func: () => onSuccess(buildFilter())
            }}
        />
    );
}

export default FilterBuilder;
